import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  analyzeAiCompetitorsWebsite,
  searchAiCompetitorsAds,
  updateAiCompetitorsSession,
} from "../aiCompetitorsApi";
import {
  AI_COMPETITORS_BASE_PATH,
  AI_COMPETITORS_ERROR_INVALID_URL,
  AI_COMPETITORS_ERROR_NO_COMPETITORS,
  AI_COMPETITORS_ERROR_WEBSITE_BLOCKED,
  AI_COMPETITORS_SESSION_CACHE_TTL_MS,
  AI_COMPETITORS_STEP_ANALYZING,
  AI_COMPETITORS_STEP_COMPETITORS,
  AI_COMPETITORS_STEP_RESULTS,
  AI_COMPETITORS_STEP_SEARCHING,
  AI_COMPETITORS_STEP_URL,
  getAiCompetitorsFoundPath,
  getAiCompetitorsSchedulePath,
} from "../aiCompetitorsConstants";
import {
  guessBrandSessionIdFromUrl,
  isAiCompetitorsSessionFresh,
  isUuidSessionId,
  readBrandQuery,
  toDomainSessionId,
} from "../aiCompetitorsBrand";
import {
  clearAiCompetitorsVisit,
  readAiCompetitorsVisit,
  rememberAiCompetitorsVisit,
} from "../aiCompetitorsSession";
import { formatCompetitorAdsItemsForGallery } from "../../../services/competitorAdsGallery";
import { adsFromAiCompetitorsSession } from "../aiCompetitorsAds";

import { LaunchError } from "../../../utils/errors";
import { store } from "../../../app/store";

const emptyBrand = {
  name: "",
  category: "",
  country: "",
  domain: "",
};

const emptyAdsSummary = {
  adsCount: 0,
  competitorCount: 0,
  competitorsWithAds: 0,
  competitorNames: [],
};

const STEP_RANK = {
  [AI_COMPETITORS_STEP_URL]: 0,
  [AI_COMPETITORS_STEP_ANALYZING]: 1,
  [AI_COMPETITORS_STEP_COMPETITORS]: 2,
  [AI_COMPETITORS_STEP_SEARCHING]: 3,
  [AI_COMPETITORS_STEP_RESULTS]: 4,
};

const sessionHasReadyAds = (session) => session?.adsSearch?.status === "done";

const countReadyAds = (ads = [], adsSummary) =>
  Math.max(Number(adsSummary?.adsCount || 0), Number(ads.length || 0));

const hasFoundAds = (ads = [], adsSummary) =>
  countReadyAds(ads, adsSummary) > 0;

const sessionHasUsefulData = (session) => {
  if (!session) return false;
  if (Array.isArray(session.competitors) && session.competitors.length > 0) {
    return true;
  }
  if (sessionHasReadyAds(session)) return true;
  const analysisStatus = session.analysis?.status;
  return Boolean(analysisStatus && analysisStatus !== "idle");
};

const deriveAiCompetitorsStep = (session) => {
  const adsStatus = session?.adsSearch?.status;
  const analysisStatus = session?.analysis?.status;
  if (adsStatus === "done") return AI_COMPETITORS_STEP_RESULTS;
  if (adsStatus === "error") return AI_COMPETITORS_STEP_SEARCHING;
  if (adsStatus && adsStatus !== "idle") return AI_COMPETITORS_STEP_SEARCHING;
  if (analysisStatus === "done") return AI_COMPETITORS_STEP_COMPETITORS;
  if (analysisStatus === "error") return AI_COMPETITORS_STEP_URL;
  if (analysisStatus && analysisStatus !== "idle") {
    return AI_COMPETITORS_STEP_ANALYZING;
  }
  return null;
};

const hydrateFromSession = (session) => {
  if (!session) return {};
  const next = {};
  if (session.websiteUrl) next.websiteUrl = session.websiteUrl;
  if (session.brand) next.brand = session.brand;
  if (Array.isArray(session.competitors) && session.competitors.length > 0) {
    next.competitors = session.competitors;
  }
  if (session.adsSummary) next.adsSummary = session.adsSummary;
  if (session.adsSearch?.status === "done") {
    const nextAds = adsFromAiCompetitorsSession(session);
    if (nextAds.length > 0) next.ads = nextAds;
  }
  return next;
};

export const useAiCompetitorsWizard = ({ language }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const brandQuery =
    searchParams.get("brand") || readBrandQuery(location.search);
  const brandSessionId = toDomainSessionId(brandQuery);
  const isFoundRoute = location.pathname.startsWith(
    getAiCompetitorsFoundPath().split("?")[0]
  );
  const stayOnCompetitorsRef = useRef(
    Boolean(readAiCompetitorsVisit()?.stayOnCompetitors)
  );
  const [sessionId, setSessionId] = useState(() => {
    if (brandSessionId) return brandSessionId;
    const visitId = readAiCompetitorsVisit()?.sessionId;
    if (visitId && !isUuidSessionId(visitId)) return visitId;
    return "";
  });
  const [step, setStep] = useState(() => {
    if (isFoundRoute && brandSessionId) {
      const visit = readAiCompetitorsVisit();
      if (hasFoundAds(visit?.ads, visit?.adsSummary)) {
        return AI_COMPETITORS_STEP_RESULTS;
      }
      return AI_COMPETITORS_STEP_COMPETITORS;
    }
    return readAiCompetitorsVisit()?.step || AI_COMPETITORS_STEP_URL;
  });
  const [websiteUrl, setWebsiteUrl] = useState(
    () => readAiCompetitorsVisit()?.websiteUrl || ""
  );
  const [brand, setBrand] = useState(
    () => readAiCompetitorsVisit()?.brand || emptyBrand
  );
  const [competitors, setCompetitors] = useState(
    () => readAiCompetitorsVisit()?.competitors || []
  );
  const [ads, setAds] = useState(() => readAiCompetitorsVisit()?.ads || []);
  const [adsSummary, setAdsSummary] = useState(
    () => readAiCompetitorsVisit()?.adsSummary || emptyAdsSummary
  );
  const [urlError, setUrlError] = useState(
    () => readAiCompetitorsVisit()?.urlError || ""
  );
  const [searchError, setSearchError] = useState(() =>
    Boolean(readAiCompetitorsVisit()?.searchError)
  );
  const [hasSnapshot, setHasSnapshot] = useState(() => {
    const visit = readAiCompetitorsVisit();
    return Boolean(visit?.competitors?.length || visit?.ads?.length);
  });
  const [hasLiveSession, setHasLiveSession] = useState(false);
  const stepRef = useRef(step);
  stepRef.current = step;

  useEffect(() => {
    if (!brandSessionId) return;
    setSessionId(brandSessionId);
  }, [brandSessionId]);

  useEffect(() => {
    if (!isFoundRoute && !brandQuery) return;
    if (isFoundRoute && !brandQuery) {
      navigate(AI_COMPETITORS_BASE_PATH, { replace: true });
    }
  }, [brandQuery, isFoundRoute, navigate]);

  useEffect(() => {
    const foundAds = hasFoundAds(ads, adsSummary);
    if (foundAds) {
      if (!isFoundRoute && sessionId && step === AI_COMPETITORS_STEP_RESULTS) {
        navigate(getAiCompetitorsFoundPath(sessionId), { replace: true });
      }
      return;
    }
    if (!isFoundRoute) return;
    const confirmedWithoutAds =
      step === AI_COMPETITORS_STEP_RESULTS ||
      (hasLiveSession && step === AI_COMPETITORS_STEP_COMPETITORS);
    if (confirmedWithoutAds) {
      navigate(AI_COMPETITORS_BASE_PATH, { replace: true });
    }
  }, [
    ads,
    adsSummary,
    hasLiveSession,
    isFoundRoute,
    navigate,
    sessionId,
    step,
  ]);

  useEffect(() => {
    rememberAiCompetitorsVisit({
      sessionId,
      step,
      websiteUrl,
      brand,
      competitors,
      ads,
      adsSummary,
      urlError,
      searchError,
      stayOnCompetitors: stayOnCompetitorsRef.current,
    });
  }, [
    ads,
    adsSummary,
    brand,
    competitors,
    searchError,
    sessionId,
    step,
    urlError,
    websiteUrl,
  ]);

  const applyHydratedSession = useCallback(
    (session, { capAtCompetitors = false } = {}) => {
      const next = hydrateFromSession(session);
      if (next.websiteUrl) setWebsiteUrl(next.websiteUrl);
      if (next.brand) {
        setBrand((current) => ({ ...current, ...next.brand }));
      }
      if (next.competitors) setCompetitors(next.competitors);
      if (next.adsSummary) {
        setAdsSummary((current) => {
          const incomingCount = Number(next.adsSummary.adsCount || 0);
          const currentCount = Number(current.adsCount || 0);
          if (incomingCount < currentCount && !next.ads) {
            return current;
          }
          return { ...current, ...next.adsSummary };
        });
      }
      if (next.ads) setAds(next.ads);
      if (session.adsSearch?.status === "error") {
        setSearchError(true);
      }
      const nextStep = deriveAiCompetitorsStep(session);
      if (!nextStep) return;
      if (
        capAtCompetitors &&
        STEP_RANK[nextStep] > STEP_RANK[AI_COMPETITORS_STEP_COMPETITORS]
      ) {
        setStep(AI_COMPETITORS_STEP_COMPETITORS);
        return;
      }
      setStep((current) =>
        STEP_RANK[nextStep] >= STEP_RANK[current] ? nextStep : current
      );
    },
    []
  );

  const onSessionSnapshot = useCallback(
    (session) => {
      if (!session || !sessionHasUsefulData(session)) return;
      const canonicalId = toDomainSessionId(
        session.websiteUrl || session.brand?.domain || ""
      );
      if (canonicalId) {
        setSessionId(canonicalId);
      }
      setHasSnapshot(true);
      setHasLiveSession(true);
      const isFresh = isAiCompetitorsSessionFresh(
        session,
        AI_COMPETITORS_SESSION_CACHE_TTL_MS
      );
      if (isFoundRoute && !isFresh && session.analysis?.status === "done") {
        // Expired cache still shows the last snapshot until the user re-analyzes.
      }
      applyHydratedSession(session, {
        capAtCompetitors:
          stayOnCompetitorsRef.current && !sessionHasReadyAds(session),
      });
    },
    [applyHydratedSession, isFoundRoute]
  );

  const onAnalyzeWebsite = useCallback(
    async (nextUrl) => {
      setUrlError("");
      setWebsiteUrl(nextUrl);
      setStep(AI_COMPETITORS_STEP_ANALYZING);
      stayOnCompetitorsRef.current = false;
      const liveSessionId = guessBrandSessionIdFromUrl(nextUrl);
      if (!liveSessionId) {
        setUrlError(AI_COMPETITORS_ERROR_INVALID_URL);
        setStep(AI_COMPETITORS_STEP_URL);
        return;
      }
      setSessionId(liveSessionId);
      try {
        const result = await analyzeAiCompetitorsWebsite({
          sessionId: liveSessionId,
          websiteUrl: nextUrl,
          language,
        });
        if (!result?.ok) {
          setUrlError(result?.code || AI_COMPETITORS_ERROR_WEBSITE_BLOCKED);
          setStep(AI_COMPETITORS_STEP_URL);
          return;
        }
        const nextBrand = result.data?.brand || emptyBrand;
        const nextCompetitors = result.data?.competitors || [];
        if (nextCompetitors.length === 0) {
          setUrlError(AI_COMPETITORS_ERROR_NO_COMPETITORS);
          setBrand(nextBrand);
          setCompetitors([]);
          setStep(AI_COMPETITORS_STEP_URL);
          return;
        }
        const nextSessionId = liveSessionId;
        const cachedSession = result.data?.session;
        const hasReadyAds = sessionHasReadyAds(cachedSession);
        const cachedAds = hasReadyAds
          ? adsFromAiCompetitorsSession(cachedSession)
          : [];
        const cachedSummary = hasReadyAds
          ? cachedSession?.adsSummary ||
            result.data?.adsSummary ||
            emptyAdsSummary
          : emptyAdsSummary;
        const cachedHasFoundAds = hasFoundAds(cachedAds, cachedSummary);
        stayOnCompetitorsRef.current = !cachedHasFoundAds;
        setBrand(nextBrand);
        setCompetitors(nextCompetitors);
        setHasSnapshot(true);
        if (result.data?.websiteUrl) {
          setWebsiteUrl(result.data.websiteUrl);
        }
        setSessionId(nextSessionId);
        if (cachedHasFoundAds && cachedSession) {
          applyHydratedSession(cachedSession);
        } else {
          setAds([]);
          setAdsSummary(emptyAdsSummary);
          setStep(AI_COMPETITORS_STEP_COMPETITORS);
        }
        rememberAiCompetitorsVisit({
          sessionId: nextSessionId,
          step: cachedHasFoundAds
            ? AI_COMPETITORS_STEP_RESULTS
            : AI_COMPETITORS_STEP_COMPETITORS,
          websiteUrl: result.data?.websiteUrl || nextUrl,
          brand: nextBrand,
          competitors: nextCompetitors,
          ads: cachedHasFoundAds ? cachedAds : [],
          adsSummary: cachedHasFoundAds ? cachedSummary : emptyAdsSummary,
          urlError: "",
          searchError: false,
          stayOnCompetitors: !cachedHasFoundAds,
        });
        if (cachedHasFoundAds) {
          navigate(getAiCompetitorsFoundPath(nextSessionId));
        }
      } catch (error) {
        store.dispatch(
          LaunchError(error, false, [], {
            functionName: "useAiCompetitorsWizard",
            capture: "exception",
          })
        );
        console.log("[analyzeInsightsCompetitorWebsite] request failed", {
          code: error?.code || error?.response?.data?.code || null,
          status: error?.response?.status || null,
        });
        setUrlError(AI_COMPETITORS_ERROR_WEBSITE_BLOCKED);
        setStep(AI_COMPETITORS_STEP_URL);
      }
    },
    [applyHydratedSession, language, navigate]
  );

  const onSearchAds = useCallback(async () => {
    setSearchError(false);
    stayOnCompetitorsRef.current = false;
    setStep(AI_COMPETITORS_STEP_SEARCHING);
    try {
      await updateAiCompetitorsSession({
        sessionId,
        competitors,
      });
    } catch (error) {
      store.dispatch(
        LaunchError(error, false, [], {
          functionName: "useAiCompetitorsWizard",
          capture: "exception",
        })
      );
      // Search persists the same list if this write fails.
    }
    try {
      const result = await searchAiCompetitorsAds({
        sessionId,
        competitors,
        language,
      });
      if (!result?.ok) {
        setSearchError(true);
        return;
      }
      const items = formatCompetitorAdsItemsForGallery(result.data?.pages);
      const summary = {
        ...(result.data?.adsSummary || {}),
        adsCount: items.length,
        competitorCount:
          result.data?.adsSummary?.competitorCount || competitors.length,
        competitorNames:
          result.data?.adsSummary?.competitorNames ||
          competitors.map((competitor) => competitor.name),
      };
      setAds(items);
      setAdsSummary(summary);
      setHasSnapshot(true);
      if (Array.isArray(result.data?.competitors)) {
        setCompetitors(result.data.competitors);
      }
      setStep(AI_COMPETITORS_STEP_RESULTS);
      if (items.length === 0) {
        navigate(AI_COMPETITORS_BASE_PATH, { replace: true });
      } else if (sessionId) {
        navigate(getAiCompetitorsFoundPath(sessionId), { replace: true });
      }
    } catch (error) {
      store.dispatch(
        LaunchError(error, false, [], {
          functionName: "useAiCompetitorsWizard",
          capture: "exception",
        })
      );
      setSearchError(true);
    }
  }, [competitors, language, navigate, sessionId]);

  const onGoToAgenda = useCallback(() => {
    updateAiCompetitorsSession({
      sessionId,
      competitors,
      booking: {
        intent: "schedule",
        adsCount: adsSummary.adsCount,
        competitorNames: adsSummary.competitorNames,
        websiteUrl,
        brandName: brand.name,
      },
    }).catch((error) => {
      store.dispatch(
        LaunchError(error, false, [], {
          functionName: "onGoToAgenda",
          capture: "exception",
          context: { sessionId },
        })
      );
    });
    navigate(getAiCompetitorsSchedulePath(sessionId), { state: { sessionId } });
  }, [
    adsSummary.adsCount,
    adsSummary.competitorNames,
    brand.name,
    competitors,
    navigate,
    sessionId,
    websiteUrl,
  ]);

  const resetLandingState = useCallback(() => {
    clearAiCompetitorsVisit();
    stayOnCompetitorsRef.current = false;
    setSessionId("");
    setStep(AI_COMPETITORS_STEP_URL);
    setWebsiteUrl("");
    setBrand(emptyBrand);
    setCompetitors([]);
    setAds([]);
    setAdsSummary(emptyAdsSummary);
    setUrlError("");
    setSearchError(false);
    setHasSnapshot(false);
    setHasLiveSession(false);
  }, []);

  const onMissingFoundSession = useCallback(() => {
    if (!isFoundRoute) return;
    resetLandingState();
    navigate(AI_COMPETITORS_BASE_PATH, { replace: true });
  }, [isFoundRoute, navigate, resetLandingState]);

  const onRestart = useCallback(() => {
    resetLandingState();
    navigate(AI_COMPETITORS_BASE_PATH);
  }, [navigate, resetLandingState]);

  const steps = useMemo(
    () => ({
      url: AI_COMPETITORS_STEP_URL,
      analyzing: AI_COMPETITORS_STEP_ANALYZING,
      competitors: AI_COMPETITORS_STEP_COMPETITORS,
      searching: AI_COMPETITORS_STEP_SEARCHING,
      results: AI_COMPETITORS_STEP_RESULTS,
    }),
    []
  );

  return {
    sessionId,
    step,
    steps,
    websiteUrl,
    brand,
    brandQuery,
    isFoundRoute,
    competitors,
    ads,
    adsSummary,
    urlError,
    searchError,
    hasSnapshot,
    setCompetitors,
    onAnalyzeWebsite,
    onSearchAds,
    onGoToAgenda,
    onRestart,
    onMissingFoundSession,
    onSessionSnapshot,
    setSearchError,
  };
};
